// AcidWashCard.jsx
import React, { useState } from "react";
import {
  Card,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  Button,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import ScienceIcon from "@mui/icons-material/Science";
import TextureIcon from "@mui/icons-material/Texture";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import InventoryIcon from "@mui/icons-material/Inventory";
import SellIcon from "@mui/icons-material/Sell";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import Matrix6 from "../../img/logos/Matrix6.png";

import { useTranslation } from "react-i18next";

import jsPDF from "jspdf";

const AcidWashCard = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();

  const handleToggle = () => setOpen((prev) => !prev);

  const handleDownloadPDF = async () => {
    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
    });

    // -------------------------------------------
    // 1️⃣ Registrar fuentes con soporte chino
    // -------------------------------------------

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const marginLeft = 40;
    const maxWidth = 520;
    let y = 50;

    // -------------------------------------------
    // 2️⃣ Cargar logo (versión correcta)
    // -------------------------------------------
    const loadImage = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.src = src;
      });

    const logo = await loadImage(Matrix6);

    // -------------------------------------------
    // 3️⃣ Header con LOGO REAL
    // -------------------------------------------
    const addHeader = () => {
      doc.addImage(logo, "PNG", marginLeft, 20, 60, 60);
      doc.setFont("SHS", "bold");
      doc.setFontSize(16);
      doc.text(t("acidWash.title"), marginLeft + 70, 55);
      y = 100;
    };

    // -------------------------------------------
    // 4️⃣ Page break seguro
    // -------------------------------------------
    const ensureSpace = (needed = 40) => {
      if (y + needed > pageHeight - 50) {
        doc.addPage();
        addHeader();
      }
    };

    // -------------------------------------------
    // 5️⃣ Title
    // -------------------------------------------
    const addTitle = (text) => {
      ensureSpace(30);
      doc.setFont("SHS", "bold");
      doc.setFontSize(16);
      doc.text(text, marginLeft, y);
      y += 24;
    };

    // -------------------------------------------
    // 6️⃣ Subtitle
    // -------------------------------------------
    const addSubtitle = (text) => {
      ensureSpace(20);
      doc.setFont("SHS", "bold");
      doc.setFontSize(13);
      doc.text(text, marginLeft, y);
      y += 18;
    };

    // -------------------------------------------
    // 7️⃣ Bullet point
    // -------------------------------------------
    const addBullet = (text) => {
      doc.setFont("SHS");
      doc.setFontSize(11);

      const lines = doc.splitTextToSize("• " + text, maxWidth);

      lines.forEach((line) => {
        ensureSpace(20);
        doc.text(line, marginLeft, y);
        y += 14;
      });

      y += 6;
    };

    // -------------------------------------------
    // 8️⃣ Generar todo el contenido
    // -------------------------------------------
    addHeader();

    addTitle("1. " + t("acidWash.standardizedRecipe"));

    addSubtitle(t("acidWash.washingParameters"));
    addBullet(t("acidWash.items.stones"));
    addBullet(t("acidWash.items.ratio"));
    addBullet(t("acidWash.items.chemicals"));
    addBullet(t("acidWash.items.temperature"));
    addBullet(t("acidWash.items.time"));
    addBullet(t("acidWash.items.rpm"));
    addBullet(t("acidWash.items.machine"));
    addBullet(t("acidWash.items.ph"));
    addBullet(t("acidWash.items.neutralization"));
    addBullet(t("acidWash.items.auxAgents"));

    addSubtitle(t("acidWash.fabricParameters"));
    addBullet(t("acidWash.items.gsm"));
    addBullet(t("acidWash.items.baseTone"));
    addBullet(t("acidWash.items.tolerances"));
    addBullet(t("acidWash.items.machineLoad"));

    addTitle("2. " + t("acidWash.mandatoryTest"));
    addBullet(t("acidWash.items.testMeters"));
    addBullet(t("acidWash.items.swatches"));
    addBullet(t("acidWash.items.submitApproval"));
    addBullet(t("acidWash.items.bulkAfterApproval"));

    addTitle("3. " + t("acidWash.shadeBands"));
    addBullet(t("acidWash.items.informativeBands"));
    addBullet(t("acidWash.items.acceptableRange"));
    addBullet(t("acidWash.items.expectations"));

    addTitle("4. " + t("acidWash.hangtag"));
    addBullet(t("acidWash.items.naturalVariations"));
    addBullet(t("acidWash.items.garmentDifferences"));
    addBullet(t("acidWash.items.irregularities"));

    addTitle("5. " + t("acidWash.colorMeasurement"));
    addBullet(t("acidWash.items.toleranceL"));
    addBullet(t("acidWash.items.toleranceA"));
    addBullet(t("acidWash.items.toleranceB"));
    addBullet(t("acidWash.items.correctionRequired"));

    addTitle("6. " + t("acidWash.fabricSegregation"));
    addBullet(t("acidWash.items.checkTone"));
    addBullet(t("acidWash.items.groupRolls"));
    addBullet(t("acidWash.items.noMixing"));

    addTitle("7. " + t("acidWash.ppSample"));
    addBullet(t("acidWash.items.cutGarments"));
    addBullet(t("acidWash.items.washApprovedRecipe"));
    addBullet(t("acidWash.items.sendPP"));
    addBullet(t("acidWash.items.noBulk"));

    addTitle("8. " + t("acidWash.consistency"));
    addBullet(t("acidWash.items.consistencyMessage"));

    // -------------------------------------------
    // 9️⃣ Guardar PDF
    // -------------------------------------------
    doc.save("AcidWash_Protocol.pdf");
  };

  const closedCardSx = {
    p: 3,
    height: "9em",
    borderRadius: 3,
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(
      theme.palette.common.white,
      0.98,
    )} 100%)`,
    boxShadow: `0 8px 22px ${alpha(theme.palette.common.black, 0.07)}`,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 14px 32px ${alpha(theme.palette.primary.main, 0.14)}`,
    },
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* CARD CERRADA */}
      {!open && (
        <Card onClick={handleToggle} elevation={0} sx={closedCardSx}>
          <Box
            component="img"
            src={Matrix6}
            alt="TON Logo"
            sx={{ width: 40, height: 40, mr: 1 }}
          />
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" }, lineHeight: 1.3 }}
          >
            {t("acidWash.title")}
          </Typography>
          <ScienceIcon sx={{ color: theme.palette.primary.main, ml: 1 }} />
        </Card>
      )}

      {/* CARD ABIERTA COMO MODAL */}
      {open && (
        <Card
          elevation={0}
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95vw", sm: "85vw", md: "75vw" },
            maxHeight: "90vh",
            overflowY: "auto",
            zIndex: 2000,
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.22)}`,
            background: "#eef2f7",
            p: 0,
          }}
        >
          {/* HEADER */}
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              px: { xs: 2, md: 3 },
              py: 1.75,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
                theme.palette.common.white,
                0.94,
              )} 72%)`,
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              backdropFilter: "blur(14px)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box
                component="img"
                src={Matrix6}
                alt="TON Logo"
                sx={{ width: 42, height: 42, flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  sx={{
                    display: "block",
                    fontWeight: 800,
                    letterSpacing: 0.8,
                    lineHeight: 1,
                    color: alpha(theme.palette.primary.main, 0.7),
                    fontSize: "0.62rem",
                  }}
                >
                  NML · Conocimiento
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.2,
                    fontSize: { xs: "0.95rem", md: "1.05rem" },
                  }}
                >
                  {t("acidWash.title")}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleDownloadPDF}
              >
                {t("acidWash.downloadPDF")}
              </Button>

              <IconButton
                onClick={handleToggle}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.grey[500], 0.1),
                  "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.18) },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Paper>

          {/* CONTENIDO */}
          <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* SECTION 1 */}
            <Typography variant="h6" sx={{ mb: 1, display: "flex", gap: 1 }}>
              <SettingsIcon />
              {t("acidWash.standardizedRecipe")}
            </Typography>

            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ mt: 2, display: "flex", gap: 1 }}
            >
              <ScienceIcon />
              {t("acidWash.washingParameters")}
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>{t("acidWash.items.stones")}</li>
              <li>{t("acidWash.items.ratio")}</li>
              <li>{t("acidWash.items.chemicals")}</li>
              <li>{t("acidWash.items.temperature")}</li>
              <li>{t("acidWash.items.time")}</li>
              <li>{t("acidWash.items.rpm")}</li>
              <li>{t("acidWash.items.machine")}</li>
              <li>{t("acidWash.items.ph")}</li>
              <li>{t("acidWash.items.neutralization")}</li>
              <li>{t("acidWash.items.auxAgents")}</li>
            </Box>

            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ mt: 2, display: "flex", gap: 1 }}
            >
              <TextureIcon />
              {t("acidWash.fabricParameters")}
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>{t("acidWash.items.gsm")}</li>
              <li>{t("acidWash.items.baseTone")}</li>
              <li>{t("acidWash.items.tolerances")}</li>
              <li>{t("acidWash.items.machineLoad")}</li>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SECTION 2 */}
            <Typography variant="h6" sx={{ mb: 1, display: "flex", gap: 1 }}>
              <CheckCircleIcon />
              {t("acidWash.mandatoryTest")}
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>{t("acidWash.items.testMeters")}</li>
              <li>{t("acidWash.items.swatches")}</li>
              <li>{t("acidWash.items.submitApproval")}</li>
              <li>{t("acidWash.items.bulkAfterApproval")}</li>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SECTION 3 */}
            <Typography variant="h6" sx={{ mb: 1, display: "flex", gap: 1 }}>
              <ColorLensIcon />
              {t("acidWash.shadeBands")}
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>{t("acidWash.items.informativeBands")}</li>
              <li>{t("acidWash.items.acceptableRange")}</li>
              <li>{t("acidWash.items.expectations")}</li>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SECTION 4 */}
            <Typography variant="h6" sx={{ mb: 1, display: "flex", gap: 1 }}>
              <SellIcon />
              {t("acidWash.hangtag")}
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>{t("acidWash.items.naturalVariations")}</li>
              <li>{t("acidWash.items.garmentDifferences")}</li>
              <li>{t("acidWash.items.irregularities")}</li>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SECTION 5 */}
            <Typography variant="h6" sx={{ mb: 1, display: "flex", gap: 1 }}>
              <ColorLensIcon />
              {t("acidWash.colorMeasurement")}
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>{t("acidWash.items.toleranceL")}</li>
              <li>{t("acidWash.items.toleranceA")}</li>
              <li>{t("acidWash.items.toleranceB")}</li>
              <li>{t("acidWash.items.correctionRequired")}</li>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SECTION 6 */}
            <Typography variant="h6" sx={{ mb: 1, display: "flex", gap: 1 }}>
              <InventoryIcon />
              {t("acidWash.fabricSegregation")}
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>{t("acidWash.items.checkTone")}</li>
              <li>{t("acidWash.items.groupRolls")}</li>
              <li>{t("acidWash.items.noMixing")}</li>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SECTION 7 */}
            <Typography variant="h6" sx={{ mb: 1, display: "flex", gap: 1 }}>
              <CheckCircleIcon />
              {t("acidWash.ppSample")}
            </Typography>

            <Box component="ul" sx={{ pl: 4 }}>
              <li>{t("acidWash.items.cutGarments")}</li>
              <li>{t("acidWash.items.washApprovedRecipe")}</li>
              <li>{t("acidWash.items.sendPP")}</li>
              <li>{t("acidWash.items.noBulk")}</li>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* SECTION 8 */}
            <Typography variant="h6" sx={{ mb: 1, display: "flex", gap: 1 }}>
              <InventoryIcon />
              {t("acidWash.consistency")}
            </Typography>

            <Typography variant="body2" sx={{ pl: 1 }}>
              {t("acidWash.items.consistencyMessage")}
            </Typography>
          </Box>
        </Card>
      )}
    </Box>
  );
};

export default AcidWashCard;
